/**
 * Instant-quote catalog — the products we can price automatically through Pax8.
 *
 * Deliberately one product at a time. Every vendor asks a different question
 * before it can be priced: CrowdStrike counts devices and sells modules as
 * separate SKUs, Microsoft 365 counts named users, Acronis prices on storage as
 * well as workloads. A single generic "how many seats?" form would produce
 * confidently wrong quotes for most of the catalog, so a product only becomes
 * instant-quotable once someone has decided what it needs to ask.
 *
 * Adding a product:
 *   1. Find its Pax8 id — `npm run pax8:find -- "<vendor or product name>"`.
 *   2. Add an entry below with the fields its quote actually depends on.
 *   3. Check the numbers against the Pax8 portal before trusting them.
 * Everything else — the badge, the form, the emailed quote — follows from this.
 *
 * A product NOT listed here still works exactly as before: the normal quote
 * form, answered by a human. This registry only decides who gets the fast path.
 */

import type { MarginBand } from './margin';

export type QuoteFieldType = 'number' | 'select' | 'text' | 'multiselect';

export interface QuoteField {
  name: string;
  label: string;
  type: QuoteFieldType;
  required?: boolean;
  /** Shown under the input — use it to remove ambiguity, not to sell. */
  help?: string;
  min?: number;
  max?: number;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  /** Default selection for a select field. */
  defaultValue?: string;
}

/**
 * What we can honestly promise a customer about leaving early.
 *
 *   'prorata'   — our upstream subscription cancels monthly, so we can stop
 *                 paying when they stop and refund the unused months.
 *   'committed' — we are locked into the full term upstream whatever the
 *                 customer does, so a refund would come out of our pocket.
 *
 * These are properties of OUR agreement with the vendor, not of how the
 * customer pays. Cynomi is billed to us monthly but commits us for a year, so
 * monthly billing upstream is not evidence of cancellability.
 *
 * 'committed' is the default on purpose. Getting this wrong in the safe
 * direction understates a benefit; getting it wrong the other way puts a
 * refund promise we cannot fund on a live customer-facing quote.
 */
export type CancellationPolicy = 'prorata' | 'committed';

export interface InstantQuoteProduct {
  /** Store slug — must match store_product_families.slug. */
  storeSlug: string;
  /** Display name used in the quote and the email. */
  productName: string;
  /**
   * Pinned Pax8 product id (UUID). Optional: when absent the API resolves it by
   * searching `pax8Search`, which is slower and can be ambiguous. Pin it once
   * you have confirmed the right catalog entry.
   */
  pax8ProductId?: string;
  /** Search string used to resolve the product when no id is pinned. */
  pax8Search: string;
  /** What one unit is called, e.g. "devices" — used in copy and the quote. */
  unitLabel: string;
  /** The field whose value is the Pax8 quantity. Must exist in `fields`. */
  quantityField: string;
  /**
   * Selectable tiers. Each pins the exact Pax8 product id whose rates apply,
   * because one pricing call returns the whole family and the rows are only
   * distinguishable by productId.
   */
  variants?: ProductVariantOption[];
  /** Name of the select field carrying the variant choice. */
  variantField?: string;
  /**
   * Defaults to 'committed'. Only set 'prorata' once someone has confirmed the
   * upstream subscription can actually be cancelled mid-term — check the vendor
   * agreement, not just that Pax8 invoices us monthly.
   */
  cancellationPolicy?: CancellationPolicy;
  /** Selectable add-ons, priced at the same quantity as the base tier. */
  addons?: ProductVariantOption[];
  /** Name of the multiselect field carrying add-on choices. */
  addonField?: string;
  /**
   * One-time professional-services engagements offerable alongside the licence.
   * Priced at their own quantity — never the seat count. See ServiceAddonOption.
   */
  serviceAddons?: ServiceAddonOption[];
  /** Name of the multiselect field carrying service choices. */
  serviceAddonField?: string;
  /**
   * Per-product margin curve, overriding DEFAULT_MARGIN_CURVE. Use it where
   * delivery cost differs — a fully managed tier carries real work on our side
   * and should not sit at the same margin as a self-managed licence.
   */
  marginCurve?: MarginBand[];
  /**
   * Overrides the billingTerm reported by Pax8 when the raw value would
   * mislead a customer. CrowdStrike's real charge arrives on rows Pax8 labels
   * "One-Time" because they are arrears SKUs, while the base subscription is
   * $0.00/month — so the charge is unambiguously recurring monthly, and
   * showing "One-Time" would understate an annual commitment ~12x.
   */
  billingTermLabel?: string;
  fields: QuoteField[];
}

export interface ProductVariantOption {
  /** Form value. */
  value: string;
  /** What the customer sees. Our naming, not the distributor's SKU name. */
  label: string;
  /**
   * Pax8 product id of the PRICING ROW this choice maps to.
   *
   * Not fetchable on its own — requesting pricing for one of these returns 403.
   * Child SKUs are only visible inside the parent product's pricing response,
   * so this id selects a row from that response rather than addressing an
   * endpoint. The parent is InstantQuoteProduct.pax8ProductId.
   */
  pricingRowProductId: string;
  /** Per-SKU override for the term shown to the customer. See
   *  InstantQuoteProduct.billingTermLabel — SKUs within one family can bill
   *  differently, so the variant's own label wins over the product default. */
  billingTermLabel?: string;
  help?: string;
}

/**
 * A fixed-scope professional-services engagement sold alongside a licence.
 *
 * WHY THIS IS A SEPARATE TYPE FROM ProductVariantOption
 * A module add-on is per-endpoint and recurring: CrowdStrike Discover is $0.97
 * an endpoint every month, so 250 endpoints is 250 × $0.97. A deployment is
 * neither. SentinelOne Deployment is $1,400 for the engagement, once, whatever
 * the endpoint count — and Pax8's pricing response cannot tell you that. Both
 * come back as `chargeType: "Per Unit"` over a single band. Push a deployment
 * through the module path and 250 endpoints quotes $350,000.
 *
 * So the distinction lives here, in a type the per-seat code cannot reach, and
 * `buildBundleQuote()` is never handed the seat count for these lines at all.
 *
 * These are also standalone Pax8 products rather than child rows of the licence
 * family, so each needs its own /pricing call — hence `pax8ProductId` is the
 * product to fetch, not a row to filter for.
 */
export interface ServiceAddonOption {
  /** Form value. */
  value: string;
  /** What the customer sees. */
  label: string;
  /** The Pax8 professional-services product to fetch pricing for. */
  pax8ProductId: string;
  /**
   * Always true. Present so that reading the entry tells you the pricing rule
   * rather than requiring you to know which array it is in.
   */
  oneTime: true;
  /**
   * Units the engagement covers. Defaults to 1 — a fixed-scope pack is one
   * engagement. Only set this where the SKU is genuinely metered (the M365
   * Email Migration SKU really is per mailbox), and never set it to the seat
   * count as a shortcut.
   */
  quantity?: number;
  /**
   * Set when Pax8's own commercial terms for this SKU are not settled. The
   * engagement stays out of live quotes until someone has confirmed it — see
   * `serviceAddonsFor()`. The string is the open question, so it travels with
   * the entry instead of living in a commit message.
   */
  unconfirmed?: string;
  help?: string;
}

/** The chosen variant, if the product has any. */
export function resolveVariant(
  product: InstantQuoteProduct,
  answers: Record<string, string>
): ProductVariantOption | undefined {
  if (!product.variants || !product.variantField) return undefined;
  return product.variants.find((v) => v.value === answers[product.variantField!]);
}

/** Selected add-ons, resolved to their pricing rows. */
export function resolveAddons(
  product: InstantQuoteProduct,
  answers: Record<string, string>
): Array<{ label: string; productId: string }> {
  if (!product.addons || !product.addonField) return [];
  const chosen = (answers[product.addonField] ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  return product.addons
    .filter((a) => chosen.includes(a.value))
    .map((a) => ({ label: a.label, productId: a.pricingRowProductId }));
}

/**
 * Selected service engagements, ready to price.
 *
 * An entry carrying `unconfirmed` is dropped rather than quoted. It still
 * reaches us on the internal notification — see the route — so the customer
 * gets a conversation about it instead of a number we are not sure we can
 * charge repeatedly. Clear the field once Pax8 has confirmed the terms; nothing
 * else has to change.
 */
export function resolveServiceAddons(
  product: InstantQuoteProduct,
  answers: Record<string, string>
): Array<{ label: string; productId: string; quantity?: number }> {
  if (!product.serviceAddons || !product.serviceAddonField) return [];
  const chosen = (answers[product.serviceAddonField] ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  return product.serviceAddons
    .filter((s) => chosen.includes(s.value) && !s.unconfirmed)
    .map((s) => ({ label: s.label, productId: s.pax8ProductId, quantity: s.quantity }));
}

/** Chosen engagements we deliberately did NOT price, with the reason. Goes on
 *  the internal notification so an unconfirmed SKU becomes a follow-up rather
 *  than silence. */
export function unconfirmedServiceAddons(
  product: InstantQuoteProduct,
  answers: Record<string, string>
): ServiceAddonOption[] {
  if (!product.serviceAddons || !product.serviceAddonField) return [];
  const chosen = (answers[product.serviceAddonField] ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  return product.serviceAddons.filter((s) => chosen.includes(s.value) && s.unconfirmed);
}

/** The pricing row id for a chosen variant, used to select from the parent's rows. */
export function resolveVariantProductId(
  product: InstantQuoteProduct,
  answers: Record<string, string>
): string | undefined {
  if (!product.variants || !product.variantField) return undefined;
  const chosen = answers[product.variantField];
  return product.variants.find((v) => v.value === chosen)?.pricingRowProductId;
}

/**
 * Asked on every instant-quote form.
 *
 * Only country. Domain is inferred from the work email, and the rest of the
 * Pax8 client record (street, city, postal code, contacts) is deliberately NOT
 * collected here: quoting uses standard pricing and needs no client, so asking
 * for a billing address before showing a price would cost conversions for
 * nothing. Those fields are collected at the point of purchase, when the
 * customer has seen the number and has a reason to fill them in.
 */
export const COUNTRY_FIELD: QuoteField = {
  name: 'country',
  label: 'Where is your organisation based?',
  type: 'select',
  required: true,
  defaultValue: 'US',
  help: 'Pricing and tax vary by country.',
  options: [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'IE', label: 'Ireland' },
    { value: 'ZA', label: 'South Africa' },
    { value: 'other', label: 'Somewhere else' },
  ],
};

/**
 * CrowdStrike — first product on the fast path.
 *
 * Chosen because it is the largest quote-intent audience we have: the
 * CrowdStrike knowledge-base category alone draws 4,658 clicks a quarter, and
 * those readers already run the product. See lib/data/software-quote-funnel.ts.
 *
 * WHAT THE PAX8 CATALOG ACTUALLY LOOKS LIKE (verified against the live API)
 * There is no "Falcon" anywhere in it. The tiers are listed as "CrowdStrike
 * MSSP Defend / Advanced Defend / Complete Defend", which is distributor
 * language, so they are presented here as Defend / Advanced / Complete.
 *
 * Pricing sits in the "- Arrears Charge" One-Time rows. The Monthly rows for
 * these SKUs return $0.00 because the product bills in arrears on usage, so
 * quoting the Monthly row would confidently offer the customer nothing at all.
 * The ids below are therefore the arrears ids, not the ids the catalog search
 * returns for the tier — that distinction is the whole reason each variant
 * pins its own id.
 */
const CROWDSTRIKE_VARIANTS: ProductVariantOption[] = [
  {
    value: 'defend',
    label: 'Defend — endpoint protection and EDR',
    pricingRowProductId: 'a2f2ac52-5a83-4dea-ada3-27ae7d8d5fce',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'advanced',
    label: 'Advanced — adds identity protection and expanded telemetry',
    pricingRowProductId: '17677ca6-65ad-4812-9a6b-d6a5d354400f',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'complete',
    label: 'Complete — fully managed detection and response',
    pricingRowProductId: 'd2b8bd33-abdd-4900-b329-6008229d44ca',
    billingTermLabel: 'Monthly',
  },
];

/**
 * Add-ons, quoted separately. Listed here so the ids are recorded and checked
 * in one place; the form asks about them but the quote currently prices the
 * base tier only — pricing a bundle means summing several rows, and that is
 * worth doing deliberately rather than as a side effect.
 */
export const CROWDSTRIKE_ADDONS: ProductVariantOption[] = [
  { value: 'discover', label: 'Discover', pricingRowProductId: 'f5dc4eb5-c29c-4a6c-ac96-dd1b8b21e976' },
  { value: 'spotlight', label: 'Spotlight', pricingRowProductId: '8de72b1f-42e0-4fbb-a8f1-e5d2fcc87efe' },
  { value: 'identity', label: 'Identity Threat Protection', pricingRowProductId: '8e12bb02-cab0-4d8b-b4e6-c15c64d8129e' },
  { value: 'mobile', label: 'Mobile', pricingRowProductId: '87f6e731-67de-47d8-88a0-cf58715c1897' },
  { value: 'data-protection', label: 'Data Protection', pricingRowProductId: '28530a78-edcf-4e4a-a9e7-d4c035db67cb' },
];

/*
 * NexGen SIEM (4418a8a1-ff9d-48b6-a344-a6e8310b50c1) is deliberately NOT
 * offered here. Its rate is ~$120 per unit against $1-3 for the add-ons above,
 * which is the signature of a product measured in something other than
 * endpoints — data volume, most likely. Multiplying it by endpoint count
 * produced $30,000/month on a 250-endpoint quote. Until someone confirms what
 * its unit actually is, it stays a manual conversation rather than an
 * automatic and badly wrong number.
 */

const CROWDSTRIKE: InstantQuoteProduct = {
  storeSlug: 'crowdstrike-falcon',
  productName: 'CrowdStrike',
  // Parent product — the only id whose /pricing endpoint we may call. Its
  // response carries every tier and add-on as separate rows.
  pax8ProductId: 'a61e1f7a-b691-43a6-b632-f9a045d97c97',
  pax8Search: 'CrowdStrike',
  unitLabel: 'endpoints',
  quantityField: 'seatCount',
  variantField: 'tier',
  variants: CROWDSTRIKE_VARIANTS,
  // Confirmed from the August invoice: billed monthly in arrears per endpoint
  // with a one-month billing period, and Pax8's own policy is that monthly
  // subscriptions renew month to month unless cancelled. So we can stop paying
  // when the customer stops.
  cancellationPolicy: 'prorata',
  addons: CROWDSTRIKE_ADDONS,
  addonField: 'addons',
  // Verified: base Defend subscription is $0.00/month and the arrears SKU
  // carries the whole price, so this is a monthly per-endpoint charge.
  billingTermLabel: 'Monthly',
  fields: [
    {
      name: 'seatCount',
      label: 'How many endpoints need protection?',
      type: 'number',
      required: true,
      min: 1,
      max: 100000,
      help: 'Count every endpoint you want covered — workstations, laptops and servers.',
      placeholder: '100',
    },
    {
      name: 'tier',
      label: 'Which tier?',
      type: 'select',
      required: true,
      defaultValue: 'defend',
      help: 'Not sure which fits? Pick Defend and we will confirm before anything is ordered.',
      options: CROWDSTRIKE_VARIANTS.map((v) => ({ value: v.value, label: v.label })),
    },
    {
      name: 'addons',
      label: 'Add any modules you need',
      type: 'multiselect',
      required: false,
      help: 'Each is priced per endpoint and added to your quote. Leave blank for the base tier only.',
      options: CROWDSTRIKE_ADDONS.map((a) => ({ value: a.value, label: a.label })),
    },
    {
      name: 'term',
      label: 'How would you like to pay?',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Month to month — cancel any time' },
        { value: 'annual', label: 'Annual, paid up front — lower rate' },
      ],
    },
    // Country is asked on every product: Pax8 prices and taxes by country, and
    // it is the one address field we cannot infer from a work email.
    COUNTRY_FIELD,
  ],
};

/**
 * SentinelOne — second product on the fast path, and the first to carry a
 * deployment engagement alongside the licence.
 *
 * VERIFIED AGAINST THE LIVE PRICING RESPONSE, 2026-08-13
 * The parent product (2e72b3b4) returns 32 rows. As with CrowdStrike the real
 * money is on the "- arrears charge" rows, and the tier names are the
 * distributor's: the endpoint tiers are listed as "Cloud Workload Security (per
 * workstation, server, & pod/task)". Control costs $2.50 falling to $1.85 with
 * volume; Complete costs $3.60 falling to $3.00. Both are Volume-model, so the
 * whole quantity prices at the single band it lands in — and the volume break
 * is real, which is exactly the case the margin curve exists to pass on.
 *
 * DELIBERATELY EXCLUDED, same reasoning as CrowdStrike's NexGen SIEM:
 *   Data Ingest (14/30/90/180/365-day)  — priced per GIGABYTE, $20–$36 a unit.
 *   Hyperautomation                     — $977.78 per "Each", unit unknown.
 *   Identity / Managed AI Defense       — priced per USER, not per endpoint.
 * Each would be multiplied by the endpoint count if offered here, and each
 * would be wrong. They stay manual conversations.
 */
const SENTINELONE_VARIANTS: ProductVariantOption[] = [
  {
    value: 'control',
    label: 'Control — endpoint protection with device and firewall control',
    pricingRowProductId: '1de0eb90-3278-4eec-9901-eaf31cd53abf',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'complete',
    label: 'Complete — adds full EDR, threat hunting and Storyline',
    pricingRowProductId: '605e2d43-5d97-4505-9c86-b326b8546d19',
    billingTermLabel: 'Monthly',
  },
];

/** Per-endpoint modules. All confirmed as unitOfMeasurement "Endpoint". */
const SENTINELONE_ADDONS: ProductVariantOption[] = [
  { value: 'vigilance', label: 'Vigilance MDR — 24/7 managed detection and response', pricingRowProductId: '09a1a1d3-dbe3-4fd5-8c21-b2c605c41e81' },
  { value: 'network-discovery', label: 'Network Discovery (formerly Ranger)', pricingRowProductId: 'e0ff5966-b272-4fd2-93a9-3b14a617ade6' },
  { value: 'vuln-management', label: 'Vulnerability Management', pricingRowProductId: '326fa5a6-761c-40d1-ae8a-1191cf9cb89f' },
  { value: 'remoteops', label: 'RemoteOps', pricingRowProductId: '280c5b7b-cfb2-4522-9d29-55af09437586' },
  { value: 'purpleai', label: 'Purple AI', pricingRowProductId: 'ca20a8b3-d987-4d79-a0b3-43839297e62e' },
];

/**
 * Deployment engagement. $1,400, once, whatever the endpoint count.
 *
 * OPEN COMMERCIAL QUESTION — this is why `unconfirmed` is set.
 * Pax8 lists it as "SentinelOne Deployment (New Partner)". Only four SKUs in
 * the 61-product professional-services catalog carry that suffix, and they are
 * all vendor deployments (Avanan, Blackpoint, Proofpoint, SentinelOne); the
 * unsuffixed engagements next to them — the Defender baselines, the Azure and
 * M365 work — do not. That pattern reads two ways. Either "New Partner" means
 * the end client is new to the platform, in which case this is a normal
 * per-customer engagement, or it means WE are a new partner to that vendor, in
 * which case it is a one-off onboarding rate we cannot sell to every customer.
 * The published Pax8 one-pager describes a per-client engagement (its
 * prerequisites are a client subscription, console access and a site token) but
 * never explains the suffix, and the sibling "SentinelOne Change of Channel"
 * SKU at $750 muddies it further.
 *
 * Until a Pax8 rep answers that, it is offered on the form and reported to us,
 * but never priced into a customer's quote. Clearing this string is the whole
 * of the change once it is confirmed.
 */
const SENTINELONE_SERVICES: ServiceAddonOption[] = [
  {
    value: 'deployment',
    label: 'Deployment — agent rollout and policy configuration',
    pax8ProductId: '29be6a7f-d17f-4f51-b331-8222bd2a0595',
    oneTime: true,
    unconfirmed:
      'Pax8 lists this as "(New Partner)". Confirm with the Pax8 rep whether ' +
      'the rate is per customer engagement or a one-off partner onboarding fee ' +
      'before quoting it.',
    help: 'A one-time engagement, quoted separately from the monthly licence.',
  },
];

const SENTINELONE: InstantQuoteProduct = {
  storeSlug: 'sentinelone',
  productName: 'SentinelOne',
  // Parent product — the only id whose /pricing endpoint we may call.
  pax8ProductId: '2e72b3b4-3dd5-46e9-8fb4-4c6898dbf035',
  pax8Search: 'SentinelOne',
  unitLabel: 'endpoints',
  quantityField: 'seatCount',
  variantField: 'tier',
  variants: SENTINELONE_VARIANTS,
  // Left at the default. SentinelOne's own catalog carries a separate "Annual
  // Commitment" SKU and its products are flagged requiresCommitment, so there
  // is no invoice-level evidence that we could stop paying mid-term the way
  // there is for CrowdStrike. Promising a refund we cannot fund is the one
  // error here that costs real money.
  cancellationPolicy: 'committed',
  addons: SENTINELONE_ADDONS,
  addonField: 'addons',
  serviceAddons: SENTINELONE_SERVICES,
  serviceAddonField: 'services',
  billingTermLabel: 'Monthly',
  fields: [
    {
      name: 'seatCount',
      label: 'How many endpoints need protection?',
      type: 'number',
      required: true,
      min: 1,
      max: 100000,
      help: 'Count every endpoint you want covered — workstations, laptops and servers.',
      placeholder: '100',
    },
    {
      name: 'tier',
      label: 'Which tier?',
      type: 'select',
      required: true,
      defaultValue: 'complete',
      help: 'Not sure which fits? Pick Complete and we will confirm before anything is ordered.',
      options: SENTINELONE_VARIANTS.map((v) => ({ value: v.value, label: v.label })),
    },
    {
      name: 'addons',
      label: 'Add any modules you need',
      type: 'multiselect',
      required: false,
      help: 'Each is priced per endpoint and added to your monthly total.',
      options: SENTINELONE_ADDONS.map((a) => ({ value: a.value, label: a.label })),
    },
    {
      name: 'services',
      label: 'Would you like us to deploy it for you?',
      type: 'multiselect',
      required: false,
      help: 'Charged once at setup, not monthly, and quoted as its own line.',
      options: SENTINELONE_SERVICES.map((s) => ({ value: s.value, label: s.label })),
    },
    {
      name: 'term',
      label: 'How would you like to pay?',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Month to month' },
        { value: 'annual', label: 'Annual, paid up front — lower rate' },
      ],
    },
    COUNTRY_FIELD,
  ],
};

/**
 * Proofpoint Essentials — email security, priced per mailbox user.
 *
 * VERIFIED AGAINST THE LIVE PRICING RESPONSE, 2026-08-13
 * The parent (9f0818c0) returns 13 rows. The tier rows carry only a One-Time
 * billingTerm, which here means the same thing it does for CrowdStrike: the one
 * SKU that publishes BOTH terms — "Beginner (END OF SALE)" — lists One-Time and
 * Monthly at the identical $1.13, and the Security Awareness Training row does
 * the same at $0.90. A One-Time row that exactly duplicates a Monthly row is a
 * monthly charge, so the tiers are labelled Monthly.
 *
 * DELIBERATELY EXCLUDED
 *   Beginner                        — Pax8 labels it END OF SALE.
 *   Archive Ingestion / Monthly Data — measured in GIGABYTES, and $0.00 besides.
 *   Trial                            — $0.00, and not what anyone asked to buy.
 *   Business+ / Advanced+ / Professional+ ($2.35 / $3.59 / $4.80) — real,
 *     current SKUs at a believable per-user rate, so these are excluded for a
 *     different reason than the rows above: nothing in the Pax8 catalog, and
 *     nothing published by Proofpoint that I could confirm, says what the "+"
 *     actually adds. Putting them on the form would mean labelling them, and
 *     the label would be a guess. Someone asking for a "+" package gets the
 *     manual form, which costs a day and quotes the right product.
 *
 * Unlike SentinelOne there are no volume bands at all — one 0-∞ band per tier —
 * so a 2,500-user quote costs us exactly what a 50-user quote does per seat.
 * The MSRP spread is also only ~32%, well under the 45/40% the margin curve
 * asks for, so the MSRP cap in priceFromCost() binds on every tier and the
 * customer price lands at Pax8's suggested retail rather than above it.
 */
const PROOFPOINT_VARIANTS: ProductVariantOption[] = [
  {
    value: 'business',
    label: 'Business — spam, phishing and malware filtering with email continuity',
    pricingRowProductId: 'fe772d8e-5b66-4a08-9550-0ddb70728661',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'advanced',
    label: 'Advanced — adds URL rewriting and attachment sandboxing',
    pricingRowProductId: '2df610c8-1d10-48e0-8227-a01086cdaed7',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'professional',
    label: 'Professional — adds email archiving and data loss prevention',
    pricingRowProductId: 'c069653f-e30e-4dee-a8dc-c3b1d32988cf',
    billingTermLabel: 'Monthly',
  },
];

/** Per-user module. Confirmed unitOfMeasurement "User", with a real Monthly row. */
const PROOFPOINT_ADDONS: ProductVariantOption[] = [
  {
    value: 'security-awareness',
    label: 'Security Awareness Training',
    pricingRowProductId: 'd53d5acf-2f73-4293-8c79-3c59de1745a3',
  },
];

const PROOFPOINT: InstantQuoteProduct = {
  storeSlug: 'proofpoint-essentials',
  productName: 'Proofpoint Essentials',
  // Parent product — the only id whose /pricing endpoint we may call.
  pax8ProductId: '9f0818c0-f626-4605-8155-22484916a76d',
  pax8Search: 'Proofpoint Essentials',
  unitLabel: 'mailboxes',
  quantityField: 'seatCount',
  variantField: 'tier',
  variants: PROOFPOINT_VARIANTS,
  // Left at the default. Nothing in the pricing response or an invoice shows
  // this subscription cancelling mid-term, and the arrears pattern alone is not
  // evidence — see the CancellationPolicy doc comment.
  cancellationPolicy: 'committed',
  addons: PROOFPOINT_ADDONS,
  addonField: 'addons',
  billingTermLabel: 'Monthly',
  fields: [
    {
      name: 'seatCount',
      label: 'How many mailboxes need protecting?',
      type: 'number',
      required: true,
      min: 1,
      max: 100000,
      help: 'Count every mailbox that sends or receives mail, including shared mailboxes.',
      placeholder: '50',
    },
    {
      name: 'tier',
      label: 'Which package?',
      type: 'select',
      required: true,
      defaultValue: 'advanced',
      help: 'Not sure which fits? Pick Advanced and we will confirm before anything is ordered.',
      options: PROOFPOINT_VARIANTS.map((v) => ({ value: v.value, label: v.label })),
    },
    {
      name: 'addons',
      label: 'Add any modules you need',
      type: 'multiselect',
      required: false,
      help: 'Priced per mailbox and added to your monthly total.',
      options: PROOFPOINT_ADDONS.map((a) => ({ value: a.value, label: a.label })),
    },
    {
      name: 'term',
      label: 'How would you like to pay?',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Month to month' },
        { value: 'annual', label: 'Annual, paid up front — lower rate' },
      ],
    },
    COUNTRY_FIELD,
  ],
};

/**
 * 1Password — business password management, priced per seat.
 *
 * VERIFIED AGAINST THE LIVE PRICING RESPONSE, 2026-08-13
 * The parent (e7f5fa35) returns 7 rows: its own Monthly row at $0.00 and six
 * "- Arrears Charge" rows, every one of them $5.00 cost against $9.99 MSRP with
 * a single 0-∞ band. So there is no tier ladder and no volume break here — the
 * six rows are the SAME product split two ways, and picking the wrong one is
 * the only mistake available:
 *
 *   Enterprise Password Manager: MSP Edition — what we resell to a customer.
 *   Internal MSP Use                          — OUR OWN licences as a partner.
 *
 * The internal-use rows must never reach a customer quote; they are the same
 * price, so a wrong pick would look completely correct on screen and be the
 * wrong SKU on the order. Each then repeats per region (US & Rest of world /
 * Canada / EU). We sell US only (lib/geo/sellable.ts), so the US row is pinned
 * and the other two are simply not referenced.
 *
 * ONE TIER, AND THE SELECT STILL EXISTS. It is not decoration: without a
 * variant the route falls back to buildQuote with no productId, which selects
 * whatever row the family returns first — the $0.00 parent, or Internal MSP
 * Use. Pinning the row is the whole safety mechanism, so the single option
 * stays and doubles as telling the customer which edition they are being
 * quoted.
 */
const ONEPASSWORD_VARIANTS: ProductVariantOption[] = [
  {
    value: 'business',
    label: 'Business — unlimited vaults, shared items and admin controls',
    // "Enterprise Password Manager: MSP Edition (US & Rest of world)".
    pricingRowProductId: '3bcd95e2-dc60-4709-b140-e790e5d8baef',
    billingTermLabel: 'Monthly',
  },
];

const ONEPASSWORD: InstantQuoteProduct = {
  storeSlug: '1password',
  productName: '1Password',
  // Parent product — the only id whose /pricing endpoint we may call.
  pax8ProductId: 'e7f5fa35-0995-42ea-9cb2-19317af32e32',
  pax8Search: '1Password',
  unitLabel: 'users',
  quantityField: 'seatCount',
  variantField: 'tier',
  variants: ONEPASSWORD_VARIANTS,
  cancellationPolicy: 'committed',
  // The parent's own subscription row is $0.00/month and the arrears SKU
  // carries the whole price, so this is a monthly per-seat charge.
  billingTermLabel: 'Monthly',
  fields: [
    {
      name: 'seatCount',
      label: 'How many people need an account?',
      type: 'number',
      required: true,
      min: 1,
      max: 100000,
      help: 'One per person. Shared vaults do not need seats of their own.',
      placeholder: '25',
    },
    {
      name: 'tier',
      label: 'Which edition?',
      type: 'select',
      required: true,
      defaultValue: 'business',
      options: ONEPASSWORD_VARIANTS.map((v) => ({ value: v.value, label: v.label })),
    },
    {
      name: 'term',
      label: 'How would you like to pay?',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Month to month' },
        { value: 'annual', label: 'Annual, paid up front — lower rate' },
      ],
    },
    COUNTRY_FIELD,
  ],
};

/**
 * Check Point Harmony Email & Collaboration — email security, per mailbox.
 *
 * THE PRODUCT IS NOT UNDER THE NAME YOU WOULD SEARCH FOR.
 * "Check Point" and "Harmony" both return nothing from the Pax8 catalog. The
 * parent is listed as **Check Point Infinity** (0f9e028d), and searching
 * "Avanan" finds it — Harmony Email is the former Avanan product. Its 34 rows
 * cover the entire Check Point line: Harmony Email, SASE, Endpoint, Mobile,
 * Browse, MDR, WAF and Workforce AI. Only the Harmony Email rows belong to this
 * store slug; the rest are separate products that would each need their own
 * entry, and are not referenced here.
 *
 * Volume model with genuine breaks — Complete (Email & Apps) falls from $6.00
 * to $3.60 per mailbox across five bands — so, as with SentinelOne, the whole
 * quantity prices at the single band it lands in and the break reaches the
 * customer through the margin curve.
 *
 * TWO TIERS COST THE SAME BELOW 500 MAILBOXES, AND THAT IS REAL.
 * Advanced (Email & Applications) and Complete (Email Only) are both $3.75 in
 * the 0-499 band, so they quote identically at 50 and at 250 mailboxes and only
 * diverge higher up ($2.75 vs $2.85 at 2,500). They are different products with
 * different MSRPs ($8.30 vs $7.70); the collision is Check Point's pricing, not
 * a row picked wrongly, and it is left alone rather than nudged apart.
 *
 * DELIBERATELY EXCLUDED — the $10.00 DMARC row (d41b8a29).
 * The family contains two DMARC SKUs: one at $0.40 per user, one at $10.00.
 * Twenty-five times its sibling is the signature of a different unit — DMARC is
 * configured per DOMAIN, and a per-domain charge multiplied by 250 mailboxes
 * quotes $2,500/month for what is almost certainly one domain. The $0.40 row is
 * offered; the $10.00 row stays a manual conversation.
 */
const CHECKPOINT_VARIANTS: ProductVariantOption[] = [
  {
    value: 'advanced-email',
    label: 'Advanced (Email only) — anti-phishing, malware and account takeover protection',
    pricingRowProductId: '0293276c-5b03-4e82-9255-e542aa6bab31',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'complete-email',
    label: 'Complete (Email only) — adds DLP and full incident remediation',
    pricingRowProductId: '4ebc0819-8ee6-4475-b375-83e9757c26bb',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'advanced-apps',
    label: 'Advanced (Email and collaboration apps) — extends cover to Teams, Slack and file sharing',
    pricingRowProductId: '89a0c42e-5ebd-46e0-9317-d387ec0a5083',
    billingTermLabel: 'Monthly',
  },
  {
    value: 'complete-apps',
    label: 'Complete (Email and collaboration apps) — everything, across email and apps',
    pricingRowProductId: '3f46691b-0eb3-45ce-ad73-0cc57f00d6be',
    billingTermLabel: 'Monthly',
  },
];

/** Per-user modules. All confirmed unitOfMeasurement "User". */
const CHECKPOINT_ADDONS: ProductVariantOption[] = [
  { value: 'archiving', label: 'Email Archiving', pricingRowProductId: '9c454402-a037-4b8e-ad3c-224993a9e2d4' },
  { value: 'dmarc', label: 'DMARC management', pricingRowProductId: '25ef0863-2fa7-48b4-9c6d-14a62027e835' },
  { value: 'incident-response', label: 'Incident Response', pricingRowProductId: '1be27f13-0bbf-4248-996d-dd1e5b7d06af' },
  { value: 'security-awareness', label: 'Security Awareness Training', pricingRowProductId: '2446032e-94d9-437d-8492-37c82d78ba87' },
  { value: 'sspm', label: 'SaaS Security Posture Management', pricingRowProductId: '1dc9cbd4-dfd3-43d3-bed2-77bb67a484e0' },
];

const CHECKPOINT: InstantQuoteProduct = {
  storeSlug: 'check-point-harmony-email',
  productName: 'Check Point Harmony Email & Collaboration',
  // Parent product — listed as "Check Point Infinity". The only id whose
  // /pricing endpoint we may call.
  pax8ProductId: '0f9e028d-70a7-4af0-996a-2c85e73c133e',
  pax8Search: 'Check Point Infinity',
  unitLabel: 'mailboxes',
  quantityField: 'seatCount',
  variantField: 'tier',
  variants: CHECKPOINT_VARIANTS,
  cancellationPolicy: 'committed',
  billingTermLabel: 'Monthly',
  addons: CHECKPOINT_ADDONS,
  addonField: 'addons',
  fields: [
    {
      name: 'seatCount',
      label: 'How many mailboxes need protecting?',
      type: 'number',
      required: true,
      min: 1,
      max: 100000,
      help: 'Count every mailbox that sends or receives mail, including shared mailboxes.',
      placeholder: '50',
    },
    {
      name: 'tier',
      label: 'Which package?',
      type: 'select',
      required: true,
      defaultValue: 'complete-email',
      help: 'Not sure which fits? Pick Complete (Email only) and we will confirm before anything is ordered.',
      options: CHECKPOINT_VARIANTS.map((v) => ({ value: v.value, label: v.label })),
    },
    {
      name: 'addons',
      label: 'Add any modules you need',
      type: 'multiselect',
      required: false,
      help: 'Priced per mailbox and added to your monthly total.',
      options: CHECKPOINT_ADDONS.map((a) => ({ value: a.value, label: a.label })),
    },
    {
      name: 'term',
      label: 'How would you like to pay?',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Month to month' },
        { value: 'annual', label: 'Annual, paid up front — lower rate' },
      ],
    },
    COUNTRY_FIELD,
  ],
};

/*
 * ACRONIS CYBER PROTECT CLOUD IS DELIBERATELY NOT HERE, and it is not a
 * judgement call — its pricing response cannot be resolved to a price at all.
 *
 * The parent (b65f05ef) returns 1,949 rows across 226 product ids. 223 of those
 * ids carry BETWEEN SIX AND NINE DIFFERENT RATE SETS UNDER THE SAME id, with no
 * field to tell them apart. "Acronis BDR - WL - Workstation (300 GB included)"
 * returns eighteen rows that are byte-for-byte identical — same productId, same
 * productName, same billingTerm, same unitOfMeasurement — differing only in
 * price, which ranges from $5.01 to $11.70. A 2.3x spread with nothing to
 * select on. These are presumably datacentre or commitment variants that Pax8
 * resolves at order time from the partner's own tenant configuration, but the
 * pricing API does not expose whatever distinguishes them.
 *
 * `selectPricing()` filters by productId and takes the first match, so wiring
 * Acronis up would quote whichever of nine prices Pax8 happened to return
 * first — a number that is right by luck and changes without warning.
 *
 * The three ids that ARE unambiguous do not rescue it: the parent row, a
 * "Minimum Commitment Adjustment Charge", and "Essential Protection" — and
 * Essential Protection is unambiguous only because all nine of its rows are
 * $0.00. There is no quotable Acronis SKU in this response.
 *
 * Acronis also mixes units freely (Workload, Seat, GB, Each) and mislabels
 * them — the per-workstation SKU above reports unitOfMeasurement "GB" — so even
 * a resolvable row would need its unit confirmed by hand.
 *
 * It stays on the manual form. Re-check if Pax8 adds a discriminator to the
 * pricing rows; nothing else about the system needs to change.
 */

/** Products on the instant-quote fast path, keyed by store slug. */
const INSTANT_QUOTE_PRODUCTS: InstantQuoteProduct[] = [
  CROWDSTRIKE,
  SENTINELONE,
  PROOFPOINT,
  ONEPASSWORD,
  CHECKPOINT,
];

const BY_SLUG = new Map(INSTANT_QUOTE_PRODUCTS.map((p) => [p.storeSlug, p]));

/** The instant-quote config for a store product, if it has one. */
export function getInstantQuoteProduct(
  storeSlug: string
): InstantQuoteProduct | undefined {
  return BY_SLUG.get(storeSlug);
}

/** The cancellation policy for a product, defaulting to the safe option. */
export function cancellationPolicyFor(
  product: InstantQuoteProduct
): CancellationPolicy {
  return product.cancellationPolicy ?? 'committed';
}

/** True when this product can be quoted automatically — drives the badge. */
export function hasInstantQuote(storeSlug: string): boolean {
  return BY_SLUG.has(storeSlug);
}

/** Every instant-quote slug, for listing pages that badge in bulk. */
export function instantQuoteSlugs(): string[] {
  return INSTANT_QUOTE_PRODUCTS.map((p) => p.storeSlug);
}

/* ===========================================================================
 * MICROSOFT — investigated 2026-08-13, deliberately NOT on the fast path.
 *
 * Microsoft 365, Intune and Defender for Endpoint were the next three products
 * queued for instant quoting. All three are blocked, and the same three
 * blockers apply to every Microsoft New Commerce Experience SKU in the Pax8
 * catalog, so the ids and the reasoning are recorded here rather than
 * rediscovered. `scripts/check-microsoft-pricing.ts` asserts all of it against
 * the real quote machinery and is the thing to re-run if any of it changes.
 *
 * 1. THE MARGIN IS 12% AND OUR FLOOR IS 15%.
 *    Every Microsoft SKU sampled — Business Basic through E5, all four Intune
 *    plans, Defender P1/P2/Server — prices at exactly 12.0% between
 *    partnerBuyRate and suggestedRetailPrice. Not approximately: 12.0% on all
 *    of them. That is the whole spread, and MINIMUM_MARGIN is 15%.
 *
 *    So there is no price that is both above our floor and at or below
 *    Microsoft's own published list. Business Premium at 250 users, monthly,
 *    in the live 'target' mode: our cost $23.23, Pax8 retail $26.40, and
 *    priceFromCost() returns $27.33 because the 15% floor overrides the MSRP
 *    cap. Add the month-to-month flexibility premium and the customer sees
 *    $31.43 against the $26.40 they can read on microsoft.com. Annual is
 *    worse: $327.95 a user a year against Microsoft's $264.
 *
 *    'msrp' mode is not an escape. It quotes $26.40 at 12.01% margin — under
 *    the floor — and the flexibility premium still lifts it to $30.36.
 *
 *    Every other vendor on the fast path has a 70%+ distributor spread, which
 *    is what the margin curve was built for. Microsoft is a different business:
 *    the licence is close to a pass-through and the money is in the managed
 *    service wrapped around it. Selling these needs a pricing policy decision
 *    (sell at list and take the 12%? bundle a managed service?) rather than a
 *    catalog entry, and it is not a decision to make inside a code change.
 *
 * 2. THERE IS NO VOLUME BREAK, SO THERE IS NOTHING TO PASS ON.
 *    Every row is `type: Flat` over a single 0–∞ band. 50 users, 250 users and
 *    2,500 users all price at exactly the same per-user rate. The margin curve
 *    tapering from 45% to 30% has no cost movement to work against, which is
 *    another sign this catalog is not the shape the pricing model assumes.
 *
 * 3. EACH EDITION IS ITS OWN PRODUCT, NOT A ROW IN A FAMILY.
 *    CrowdStrike and SentinelOne return their whole family from one /pricing
 *    call, which is why a variant pins a `pricingRowProductId` and the product
 *    pins the parent. Microsoft has no parent: Business Premium's /pricing
 *    returns only Business Premium. Wiring these would mean a variant carrying
 *    its own product id and the route fetching pricing per variant — a real
 *    change to the shared shapes, and not worth making for products blocked on
 *    (1) regardless.
 *
 * PLUS TWO TRAPS THAT WILL BITE WHOEVER PICKS THIS UP
 *
 *   The Monthly row is not the month-to-month price. Microsoft sells
 *   commitment and billing frequency as separate axes, so a SKU has both a
 *   bare `Monthly` row and a `Monthly` row with `commitmentTerm: 'Monthly'`.
 *   The bare one is the ANNUAL-commitment rate billed monthly. selectPricing()
 *   matches on billingTerm alone and takes the first hit, so Business Standard
 *   "month to month" selects the $11.00 annual-commitment row rather than the
 *   $14.78 true month-to-month row — quoting a year's lock-in as "cancel any
 *   time", at a price that only exists if the customer cannot.
 *
 *   unitOfMeasurement lies. Pax8 reports "User" for Intune Plan 1 Device,
 *   which is licensed per device, and "User" for Defender for Endpoint P1/P2,
 *   which Microsoft licenses per named user covering up to five devices each.
 *   Asking "how many endpoints?" and passing the answer as the quantity
 *   over-counts licences for any organisation with more than one device per
 *   person. Intune counts devices only on the Device SKU; Defender for
 *   Endpoint counts people, not endpoints, and the field must say so.
 *
 * The ids below are verified against the live catalog and are the commercial
 * (non-profit / education / GCC excluded) SKUs. Nothing imports them yet; they
 * are here so that enabling Microsoft is a pricing decision plus wiring, not a
 * fresh catalog dig.
 * ========================================================================= */

/**
 * A standalone Pax8 product, as opposed to ProductVariantOption's pricing row
 * inside a parent's response. Microsoft has no parent products, so its tiers
 * cannot be expressed as variants — see blocker 3 above.
 */
export interface StandaloneSku {
  label: string;
  /** Pax8 product id. Its /pricing response contains only this SKU. */
  pax8ProductId: string;
  /** What Microsoft actually licenses, which is not always what Pax8 reports. */
  licensedPer: 'user' | 'device' | 'server';
  /**
   * Pax8 suggestedRetailPrice on the `Monthly` / `1-Year` commitment row — the
   * normal MSP sale, an annual commitment billed monthly.
   *
   * Note this is Pax8's number, not Microsoft's. On the bare `Monthly` and
   * `Annual` rows Pax8's retail matches Microsoft's published list exactly
   * ($26.40 and $264.00 for Business Premium); on the commitment-tagged rows it
   * sits about 5% over it ($23.10 against Microsoft's $22.00). So the customer's
   * reference price is the lower of the two, and the margin available against
   * what they can look up themselves is thinner still than the 12% below.
   */
  retailMonthly: number;
  /** Our cost, on the same row. */
  costMonthly: number;
}

export const MICROSOFT_365_SKUS: StandaloneSku[] = [
  { label: 'Business Basic', pax8ProductId: '9c773c32-9871-4a12-a592-b2ee129fef61', licensedPer: 'user', retailMonthly: 7.35, costMonthly: 6.468 },
  { label: 'Business Standard', pax8ProductId: '9e8ff14a-a4ae-441a-af3d-9b23b3ab01be', licensedPer: 'user', retailMonthly: 14.7, costMonthly: 12.936 },
  { label: 'Business Premium', pax8ProductId: '05df4303-8948-4198-ac48-33c27d044f69', licensedPer: 'user', retailMonthly: 23.1, costMonthly: 20.328 },
  { label: 'E3', pax8ProductId: 'cb99a83e-0983-4580-b8fe-e0ce95db8637', licensedPer: 'user', retailMonthly: 40.95, costMonthly: 36.036 },
  { label: 'E5', pax8ProductId: '5a5d2477-e54d-4a86-8f02-6e787b491a23', licensedPer: 'user', retailMonthly: 63, costMonthly: 55.44 },
];

export const MICROSOFT_INTUNE_SKUS: StandaloneSku[] = [
  { label: 'Intune Plan 1 (per user)', pax8ProductId: 'd2cad44b-95d3-4ce0-9819-93e74d1e5c7d', licensedPer: 'user', retailMonthly: 8.4, costMonthly: 7.392 },
  // Pax8 reports unitOfMeasurement "User" on this one. It is per device.
  { label: 'Intune Plan 1 (per device)', pax8ProductId: 'f819cf34-77fc-4f23-9a76-e7fc071d1e98', licensedPer: 'device', retailMonthly: 2.84, costMonthly: 2.4992 },
  // Plan 2 and Suite are ADD-ONS to Plan 1, not alternatives to it. Offering
  // them as a tier choice would quote a customer an add-on with nothing to add
  // it to.
  { label: 'Intune Plan 2 add-on', pax8ProductId: 'f80501ca-f2b6-4bc4-aacb-ba6383ff48a7', licensedPer: 'user', retailMonthly: 4.2, costMonthly: 3.696 },
  { label: 'Intune Suite add-on', pax8ProductId: '92fd7344-abfa-46af-a56d-5ac2026d8fd5', licensedPer: 'user', retailMonthly: 10.5, costMonthly: 9.24 },
];

export const MICROSOFT_DEFENDER_ENDPOINT_SKUS: StandaloneSku[] = [
  // P1 and P2 are per NAMED USER covering up to five devices each, despite
  // Pax8 reporting "User" as loosely as it reports it for device SKUs.
  { label: 'Defender for Endpoint P1', pax8ProductId: 'a32169c1-fbcb-43e3-8f45-b0f9f8b16a09', licensedPer: 'user', retailMonthly: 3.15, costMonthly: 2.772 },
  { label: 'Defender for Endpoint P2', pax8ProductId: '195e97a7-ab6a-4f0d-9f33-b3de1fe47cf3', licensedPer: 'user', retailMonthly: 5.46, costMonthly: 4.8048 },
  // The one Microsoft SKU here that genuinely counts machines.
  { label: 'Defender for Endpoint Server', pax8ProductId: '9e0a3667-d304-4470-b5dc-af183d7d566c', licensedPer: 'server', retailMonthly: 5.46, costMonthly: 4.8048 },
  // Sold only to organisations under 300 seats. That eligibility rule is not
  // in the pricing response, so quoting it needs a seat-count guard the form
  // does not currently have.
  { label: 'Defender for Business (under 300 seats)', pax8ProductId: '5b44a6b1-a3f0-4cc0-8b86-0a0cac618ffd', licensedPer: 'user', retailMonthly: 3.15, costMonthly: 2.772 },
];

/** The one margin every Microsoft NCE SKU carries. Below MINIMUM_MARGIN. */
export const MICROSOFT_NCE_MARGIN = 0.12;
